// Shared wording and pills for the Returns page and the Orders page panel.
import type { ReturnStatus, ReturnEvent } from "../../api/returns";

type T = (key: string, params?: Record<string, string>) => string;

const TONE: Record<ReturnStatus, string> = {
    review: "amber",
    label_pending: "orange",
    awaiting_return: "blue",
    exchange_booked: "blue",
    in_transit: "teal",
    received: "purple",
    refund_pending: "green",
    replacement_sent: "green",
    completed: "green",
    cancelled: "grey",
};

export const StatusPill = ({ status, t }: { status: ReturnStatus; t: T }) => (
    <span className={`rt-pill rt-pill--${TONE[status] ?? "grey"}`}>{t(`rt_status_${status}`)}</span>
);

export const problemLabel = (t: T, problem: string) => t(`rt_problem_${problem}`);
export const resolutionLabel = (t: T, resolution: string) => t(`rt_res_${resolution}`);

// Events recorded against a request (return_events.return_id). Labels and
// exchanges add their own later; those show by name until they get wording.
const KNOWN_EVENTS = new Set([
    "request_submitted", "refund_pending", "replacement_created",
    "staff_approved", "staff_declined", "staff_completed", "staff_cancelled", "staff_note",
    "customer_cancelled", "label_void_failed", "exchange_shortfall",
    "apc_voided", "apc_void_manifested", "apc_void_failed", "labels_voided",
    "pool_label_issued", "pool_label_specimen", "pool_label_downloaded", "pool_label_released", "pool_label_kept",
    "replacement_held", "replacement_released", "replacement_cancelled", "postage_charged", "refund_issued", "refund_declined",
    "staff_created", "superseded", "exchange_booked", "label_booked", "payment_refunded",
]);

/** An event without its own words: "parcel_delivered" → "Parcel delivered". */
const plainEvent = (event: string) => {
    const words = event.replace(/_/g, " ");
    return words.charAt(0).toUpperCase() + words.slice(1);
};

/** How a held (auto-approved) replacement was released: the return's milestone, or staff; raw when unknown. */
export const replacementViaText = (t: T, via: string): string =>
    via === "staff" ? t("rt_rep_via_staff") : ["shipped", "delivered", "received"].includes(via) ? t(`rt_exc_via_${via}`) : via;

/** A timeline line for one return_events row: what happened, then any detail worth a line. */
export const eventText = (t: T, e: ReturnEvent): { title: string; detail: string | null } => {
    const d = e.detail ?? {};
    const title = KNOWN_EVENTS.has(e.event) ? t(`rt_event_${e.event}`) : plainEvent(e.event);
    let detail: string | null = null;
    if (e.event === "staff_approved") detail = t("rt_event_approved_as", { resolution: t(`rt_res_${d.resolution ?? "refund"}`) });
    else if (e.event === "staff_declined" || e.event === "staff_completed") detail = d.message ?? null;
    else if (e.event === "staff_cancelled") detail = d.reason ?? null;
    else if (e.event === "staff_note") detail = d.text ?? null;
    else if (e.event === "customer_cancelled") detail = Array.isArray(d.parcels_to_void) && d.parcels_to_void.length ? t("rt_event_customer_cancelled_labels", { n: String(d.parcels_to_void.length) }) : null;
    else if (e.event === "label_void_failed") detail = d.error ? String(d.error) : null;
    else if (e.event === "exchange_shortfall" && typeof d.shortfall_pence === "number") detail = `£${(d.shortfall_pence / 100).toFixed(2)}`;
    else if (e.event === "apc_voided" || e.event === "apc_void_manifested" || e.event === "apc_void_failed") detail = [d.waybill, d.error].filter(Boolean).join(" · ") || null;
    else if (e.event === "labels_voided") detail = Array.isArray(d.consignments) && d.consignments.length ? d.consignments.map((c: any) => `${c.waybill} ${c.outcome}`).join(", ") : (d.parcels ? t("rt_event_labels_voided_parcels", { n: String(d.parcels) }) : null);
    else if (e.event.startsWith("pool_label_")) detail = [d.tracking, d.mailmark_ref].filter(Boolean).join(" · ") || null;
    else if (e.event === "replacement_created") detail = d.replacement_number ?? d.replacement_order_id ?? null;
    else if (e.event === "refund_pending" && typeof d.amount_pence === "number") detail = `£${(d.amount_pence / 100).toFixed(2)}`;
    else if (e.event === "replacement_released" && d.released_via) detail = replacementViaText(t, String(d.released_via));
    else if ((e.event === "postage_charged" || e.event === "refund_issued") && typeof d.amount_pence === "number") detail = `£${(d.amount_pence / 100).toFixed(2)}`;
    else if (e.event === "refund_declined") detail = d.decline_reason ? String(d.decline_reason) : null;
    else if (e.event === "payment_refunded") detail = d.reason ? String(d.reason) : null;
    else if (e.event === "request_submitted" && d.auto_action_failed) detail = t("rt_event_auto_failed", { why: String(d.auto_action_failed) });
    // New return: who made it and their note; a request it replaced names the new one.
    else if (e.event === "staff_created") detail = [d.by_name, d.note].filter(Boolean).map(String).join(" · ") || null;
    else if (e.event === "superseded") detail = d.by_reference ? String(d.by_reference) : null;
    return { title, detail };
};
